import React, {ComponentProps} from "react";
import {useTranslation} from "react-i18next";

import Table from "../../../ui/table";

import cn from "classnames";
import classes from "./OfflineTestSessionsTable.module.css";
import {formatDateTime} from "../../../time";
import {QueryData} from "../../../scenes/account/offline-test-sessions.graphql";
import {PlayIcon} from "../../../ui/icons";
import {gql} from "@apollo/client";
import Link from "../../../ui/link";
import {CompetenceAnalysisState, CompetenceResult, OfflineTestSession} from "../../../schema";


export type Props = Omit<ComponentProps<typeof Table>, "children"> & {
  sessions: QueryData["getOfflineTestSessions"];
  onPlayButtonClick: (event: React.MouseEvent) => void;
  onCompetenceResultsClick: (competenceResults: CompetenceResult[]) => void;
};

export default function OfflineTestSessionsTable({sessions,
                                                   onPlayButtonClick,
                                                   onCompetenceResultsClick,
                                                   ...props}: Props) {
  const {t} = useTranslation();
  const competenceStateI18nMap: Record<CompetenceAnalysisState, string> = {
    [CompetenceAnalysisState.NOT_STARTED]:
      t("components.OfflineTestSessionsTable.state.NOT_STARTED"),
    [CompetenceAnalysisState.IN_PROGRESS]:
      t("components.OfflineTestSessionsTable.state.IN_PROGRESS"),
    [CompetenceAnalysisState.READY]:
      t("components.OfflineTestSessionsTable.state.READY"),
    [CompetenceAnalysisState.ERROR]:
      t("components.OfflineTestSessionsTable.state.ERROR"),
    [CompetenceAnalysisState.UNKNOWN]:
      t("components.OfflineTestSessionsTable.state.UNKNOWN"),
  };

  return (
    <div className={cn(classes.root)}>
      <Table className={cn(classes.table)} {...props}>
        <Table.Head>
          <Table.Row>
            <Table.Cell>{t("components.OfflineTestSessionsTable.name")}</Table.Cell>
            <Table.Cell>{t("components.OfflineTestSessionsTable.user")}</Table.Cell>
            <Table.Cell>{t("components.OfflineTestSessionsTable.challenge")}</Table.Cell>
            <Table.Cell>{t("components.OfflineTestSessionsTable.audio")}</Table.Cell>
            <Table.Cell>{t("components.OfflineTestSessionsTable.competenceResults")}</Table.Cell>
            <Table.Cell>{t("components.OfflineTestSessionsTable.analyzedAt")}</Table.Cell>
            <Table.Cell>{t("components.OfflineTestSessionsTable.analysisState")}</Table.Cell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {sessions.map((session: OfflineTestSession) => (
            <Table.Row key={session.uuid}>
              <Table.Cell>
                <span className={classes.sessionName}>{session.name || session.uuid}</span>
              </Table.Cell>
              <Table.Cell>
                {session.user ? `${session.user.fullName}` : "-"}
              </Table.Cell>
              <Table.Cell>{session.challenge.name}</Table.Cell>
              <Table.Cell>
                {session.audio ? (
                  <Link
                    className={classes.audioButton}
                    data-id={session.audio}
                    onClick={onPlayButtonClick}
                  >
                    <PlayIcon className={classes.playIcon} />
                    {t("components.OfflineTestSessionsTable.playAudio")}
                  </Link>
                ) : (
                  "-"
                )}
              </Table.Cell>
              <Table.Cell>
                {session.competenceAnalysisState === CompetenceAnalysisState.READY &&
                session.competenceResults &&
                session.competenceResults.length > 0 ? (
                  <Link
                    className={classes.competenceResultsLink}
                    onClick={() => onCompetenceResultsClick(session.competenceResults || [])}
                  >
                    <div className={classes.competenceResults}>
                      {session.competenceResults.map((result: CompetenceResult, index: number) => (
                        <div key={index} className={classes.competenceResult}>
                          <span className={classes.competenceName}>{result.competence || ""}:</span>
                          <span className={classes.competenceScore}>
                            {Math.round((result.score || 0) * 100)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </Link>
                ) : session.competenceAnalysisState === CompetenceAnalysisState.IN_PROGRESS ? (
                  <span className={classes.inProgress}>{t("components.OfflineTestSessionsTable.inProgress")}</span>
                ) : (
                  "-"
                )}
              </Table.Cell>
              <Table.Cell>
                {session.analyzedAt ? formatDateTime(session.analyzedAt) : "-"}
              </Table.Cell>
              <Table.Cell>
                <span
                  className={cn(classes.stateBadge, {
                    [classes.stateNotStarted]:
                      session.competenceAnalysisState === CompetenceAnalysisState.NOT_STARTED,
                    [classes.stateInProgress]:
                      session.competenceAnalysisState === CompetenceAnalysisState.IN_PROGRESS,
                    [classes.stateReady]:
                      session.competenceAnalysisState === CompetenceAnalysisState.READY,
                    [classes.stateError]:
                      session.competenceAnalysisState === CompetenceAnalysisState.ERROR,
                  })}
                >
                  {competenceStateI18nMap[session.competenceAnalysisState]}
                </span>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
}

OfflineTestSessionsTable.fragments = {
  offlineTestSessions: gql`
    fragment OfflineTestSessions on OfflineTestSession {
      id
      uuid
      name
      user {
        id
        fullName
        email
      }
      challenge {
        id
        name
      }
      audio
      competenceResults {
        competence
        score
        feedback
        indicators {
          indicator
          isPresent
          example
        }
      }
      competenceAnalysisState
      analyzedAt
    }
  `,
};

